import React, { useCallback, useEffect, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Globe, Copy, CheckCircle2, Mail, Trash2, Star } from "lucide-react";
import { canonicalPublicUrl } from "@/lib/publicUrl";

export default function LeadPortalSection({ lead, onChanged }) {
    const [proposals, setProposals] = useState([]);
    const [link, setLink] = useState(null);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);
    const [props, setProps] = useState([]);
    const [selProp, setSelProp] = useState("");

    const load = useCallback(async () => {
        try { const r = await api.get(`/crm/leads/${lead.id}/proposals`); setProposals(r.data.items || []); } catch (e) { console.error(e); }
    }, [lead.id]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/me/properties").then((r) => setProps(((r.data.items || r.data) || []).filter((p) => p.status === "active"))).catch(() => {});
    }, []);

    const generate = async (sendEmail) => {
        setBusy(true);
        try {
            const r = await api.post(`/crm/leads/${lead.id}/portal`, { send_email: sendEmail });
            setLink(canonicalPublicUrl(r.data.portal_link));
            if (sendEmail) window.alert(r.data.email_sent ? "Link portale inviato via email al cliente." : "Portale attivo. Copia il link e condividilo.");
            onChanged?.();
        } catch (e) {
            if (e?.response?.status === 403) window.alert(e.response.data?.detail || "Permesso negato");
        } finally { setBusy(false); }
    };

    const propose = async () => {
        if (!selProp) return;
        try {
            await api.post(`/crm/leads/${lead.id}/proposals`, { property_id: selProp });
            setSelProp("");
            await load(); onChanged?.();
        } catch (e) { window.alert(e?.response?.data?.detail || "Errore"); }
    };

    const removeProposal = async (id) => {
        await api.delete(`/crm/proposals/${id}`).catch(() => {});
        await load();
    };

    return (
        <div data-testid="lead-portal-section" className="border border-white/10 bg-ink-900/40 p-4 space-y-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-400"><Globe size={13} className="text-gold" /> Portale cliente</div>
            <div className="flex gap-2">
                <button data-testid="portal-generate" onClick={() => generate(false)} disabled={busy}
                    className="flex-1 cta-outline py-2 text-[11px]">{busy ? "..." : link || lead.portal_token ? "Mostra link portale" : "Attiva portale"}</button>
                <button data-testid="portal-send-email" onClick={() => generate(true)} disabled={busy || !lead.email}
                    className="flex-1 cta-gold py-2 text-[11px] flex items-center justify-center gap-1.5 disabled:opacity-40"><Mail size={12} /> Invia al cliente</button>
            </div>
            {link && (
                <div className="flex items-center gap-2 border border-gold/30 bg-gold/5 px-3 py-2">
                    <span data-testid="portal-link" className="flex-1 text-[10px] text-ink-100 truncate">{link}</span>
                    <button data-testid="portal-copy" onClick={() => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                        className="text-gold hover:text-ink-50 flex items-center gap-1 text-[10px]">
                        {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}{copied ? "Copiato" : "Copia"}
                    </button>
                </div>
            )}

            {lead.kind !== "seller" && (
                <div className="pt-2 border-t border-white/10">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-2">Immobili proposti ({proposals.length})</div>
                    <div className="flex gap-2 mb-2">
                        <select data-testid="proposal-select" value={selProp} onChange={(e) => setSelProp(e.target.value)}
                            className="flex-1 bg-ink-950 border border-white/15 px-2 py-1.5 text-[11px] text-ink-50 outline-none">
                            <option value="">— Scegli un immobile da proporre —</option>
                            {props.map((p) => <option key={p.id} value={p.id}>{p.title} · {formatPrice(p.price)}</option>)}
                        </select>
                        <button data-testid="proposal-add" onClick={propose} disabled={!selProp} className="cta-outline px-3 text-[11px] disabled:opacity-40">Proponi</button>
                    </div>
                    {proposals.map((p) => (
                        <div key={p.id} className="flex items-center gap-2 py-1.5 text-[11px] border-b border-white/5">
                            <span className="flex-1 text-ink-100 truncate">{p.property_title}</span>
                            {p.feedback ? (
                                <span data-testid={`proposal-fb-${p.id}`} className={`shrink-0 flex items-center gap-1 text-[10px] px-1.5 py-0.5 border ${p.feedback.interested ? "border-green-700/40 text-green-300" : "border-red-700/40 text-red-300"}`}>
                                    {p.feedback.interested ? "Interessato" : "No"}{p.feedback.rating ? <><Star size={9} className="fill-current" />{p.feedback.rating}</> : null}
                                </span>
                            ) : (
                                <span className="shrink-0 text-[10px] text-ink-500">In attesa</span>
                            )}
                            <button data-testid={`proposal-remove-${p.id}`} onClick={() => removeProposal(p.id)} className="text-ink-500 hover:text-red-300"><Trash2 size={11} /></button>
                        </div>
                    ))}
                    {proposals.length === 0 && <div className="text-[10px] text-ink-500">Nessun immobile ancora proposto a questo cliente.</div>}
                </div>
            )}
        </div>
    );
}
